import { AfterViewInit, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { ToastyService } from "ng2-toasty";
import { OscService } from "../../core/services/proponente/osc.service";
import { ErrorHandlerService } from "./../../core/error-handler.service";
import { NotificacaoHasUsuario, Usuario } from "./../../core/model";
import { CelebracaoService } from "./../../core/services/celebracao/celebracao.service";
import { ConcedenteService } from "./../../core/services/concedente/concedente.service";
import {
  NotificacaoHasUsuarioFiltro,
  NotificacaoHasUsuarioService,
} from "./../../core/services/notificacao/notificacaoHasUsuario.service";
import { TrocaEntidadeService } from "../../core/services/troca-entidade.service";
import { AuthService } from "./../../seguranca/auth.service";
import { UsuarioService } from "./../../usuario/usuario.service";
import { nextRender, safeUpdate } from "../../core/utils/change-detection-safe";

declare let AdminLTE: any;

@Component({
  standalone: false,
  selector: "app-home",
  templateUrl: "./home-page.component.html",
  styleUrls: ["./home-page.component.css"],
})
export class HomePageComponent implements OnInit, AfterViewInit {
  filtroNotificacaoHasUsuario = new NotificacaoHasUsuarioFiltro();
  notificacaoHasUsuario = new NotificacaoHasUsuario();
  listaNotificacao = [];
  carregandoNotificacoes = true;
  openModalAviso = false;
  senha: string;
  modalSenhaFraca = false;
  senhaAtual: string;
  novaSenha: string;
  confirmarSenha: string;
  senhaValida: boolean = false;
  usuario: Usuario = new Usuario();
  hoveredIcon: string = '';
  homeCarregada = false;
  carregandoHome = true;
  exibirCarregamentoHome = true;
  exibirConteudoHome = false;

  constructor(
    public auth: AuthService,
    private title: Title,
    private notificacaoHasUsuarioService: NotificacaoHasUsuarioService,
    private usuarioService: UsuarioService,
    private proponentesService: OscService,
    private celebracaoService: CelebracaoService,
    private concedenteService: ConcedenteService,
    private router: Router,
    private toasty: ToastyService,
    private errorHandler: ErrorHandlerService,
    private trocaEntidadeService: TrocaEntidadeService,
    private cdRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    AdminLTE.init();
    this.title.setTitle("Home");

    this.senhaAtual = "";
    this.novaSenha = "";
    this.confirmarSenha = "";
  }

  ngAfterViewInit(): void {
    nextRender(() => this.carregarDadosIniciais());
  }

  private carregarDadosIniciais() {
    this.homeCarregada = false;
    this.carregandoHome = true;
    this.exibirCarregamentoHome = true;
    this.exibirConteudoHome = false;

    Promise.all([
      this.usuarioService.countUsuario().catch(() => 0),
      this.proponentesService.countEntidade().catch(() => 0),
      this.concedenteService.countEntidade().catch(() => 0),
      this.celebracaoService.countProjetos().catch(() => 0),
    ]).then(([usuarios, proponentes, concedentes, projetos]) => {
      safeUpdate(() => {
        this.countUsuarios = usuarios || 0;
        this.countProponentes = proponentes || 0;
        this.countConcedentes = concedentes || 0;
        this.countProjetos = projetos || 0;
        this.carregandoHome = false;
        this.homeCarregada = true;
        this.exibirCarregamentoHome = false;
        this.exibirConteudoHome = true;
        this.cdRef.detectChanges();
      });
    });

    this.listarNotificacoesHasUser();
    if (sessionStorage.getItem("urlLogin")) {
      sessionStorage.removeItem("urlLogin");
      this.exigirTrocaDeSenhaSeNecessario();
    
    }
  }

  /**
   * A troca obrigatória de senha, no primeiro acesso depois de marcada.
   *
   * Antes, esta janela abria quando a senha digitada no login tinha 3 caracteres ou menos —
   * era o jeito de alcançar quem ainda tinha os três dígitos do CPF, e dependia de guardar a
   * senha em claro no `sessionStorage`. Quem decide agora é o servidor, pelo campo
   * `deve_trocar_senha` que vem no token: ele cobre tanto as senhas antigas quanto as
   * sorteadas num cadastro ou num reset, e a senha em claro não precisa mais ser guardada.
   */
  exigirTrocaDeSenhaSeNecessario() {
    this.trocaEntidadeService.solicitarAbertura();
    sessionStorage.removeItem("password");

    if (this.auth.jwtPayload && this.auth.jwtPayload.deve_trocar_senha === true) {
      this.modalSenhaFraca = true;
      this.usuarioService.buscarPorCodigo(this.auth.jwtPayload.id)
        .then((usuario: Usuario) => {
          this.usuario = usuario;
        })
        .catch((erro) => this.errorHandler.handle(erro));
    }
  }

  /**
   * O que ainda falta na senha nova, enquanto se digita.
   *
   * A mesma lista da tela de perfil, e **a decisão continua sendo do servidor**: esta cópia
   * existe para dar retorno imediato, não para julgar.
   */
  get problemasDaSenha(): string[] {
    const senha = this.novaSenha || "";
    const faltando: string[] = [];

    if (!senha) { return faltando; }
    if (senha.length < 8) { faltando.push("pelo menos 8 caracteres"); }
    if (!/[A-Z]/.test(senha)) { faltando.push("uma letra maiúscula"); }
    if (!/[a-z]/.test(senha)) { faltando.push("uma letra minúscula"); }
    if (!/[0-9]/.test(senha)) { faltando.push("um número"); }
    if (!/[^A-Za-z0-9]/.test(senha)) { faltando.push("um caractere especial"); }
    return faltando;
  }

  get podeAlterarSenha(): boolean {
    return !!this.senhaAtual
      && !!this.novaSenha
      && this.problemasDaSenha.length === 0
      && this.novaSenha === this.confirmarSenha;
  }

  alterarSenha() {
    if (!this.podeAlterarSenha) {
      this.toasty.error(
        "As senhas não coincidem ou a nova senha ainda não cumpre a política."
      );
      return;
    }

    this.usuario.senha = this.novaSenha;
    this.usuario.senha_antiga = this.senhaAtual;

    // `/usuarios/atualizar-senha` e não `/usuarios/{id}`: é a porta que confere a senha
    // atual e baixa a marca de troca obrigatória. Salvar pelo cadastro inteiro deixaria a
    // marca de pé, e esta janela voltaria a cada acesso.
    this.usuarioService
      .atualizarSenha(this.usuario)
      .then((status) => {
        if (status === 204) {
          this.errorHandler.handle("A senha atual não confere.");
          return;
        }
        this.toasty.success(
          "Senha alterada com sucesso! Acesse o sistema novamente para que as alterações tenham efeito."
        );
        this.modalSenhaFraca = false;
        this.senhaAtual = "";
        this.novaSenha = "";
        this.confirmarSenha = "";
      })
      .catch((erro) => this.errorHandler.handle(erro));
  }

  //==================Listagem dos Cards
  countUsuarios = 0;
  countProponentes = 0;
  countConcedentes = 0;
  countProjetos = 0;
  //===========.Fim listagem

  listarNotificacoesHasUser() {
    this.carregandoNotificacoes = true;
    this.filtroNotificacaoHasUsuario.itensPorPagina = 5;
    this.filtroNotificacaoHasUsuario.usuario = this.auth.jwtPayload.id;
    this.notificacaoHasUsuarioService
      .pesquisarResumo(this.filtroNotificacaoHasUsuario)
      .then((lista) => {
        safeUpdate(() => {
          this.listaNotificacao = lista.content;
          this.carregandoNotificacoes = false;
          this.cdRef.detectChanges();
        });
      })
      .catch(() => {
        safeUpdate(() => {
          this.carregandoNotificacoes = false;
          this.cdRef.detectChanges();
        });
      });
  }

  setaNotificacaoSessao(notificacao) {
    // A listagem da home usa uma projecao leve (sem o objeto completo), entao
    // buscamos o registro completo por id antes de marcar como lida/visualizar,
    // mantendo o fluxo de atualizacao (PUT) e a tela de visualizacao intactos.
    this.notificacaoHasUsuarioService
      .buscarPorCodigo(notificacao.id)
      .then((completo: any) => {
        if (completo.statusNotificacao === "Lida") {
          sessionStorage.setItem("verNotificacao", JSON.stringify(completo));
          this.router.navigate(["/notificacao/visualizar"]);
        } else {
          completo.statusNotificacao = "Lida";
          this.notificacaoHasUsuarioService
            .atualizar(completo)
            .then((resultado) => {
              this.toasty.info("Notificacao atualizada para visualizada.");
              sessionStorage.setItem("verNotificacao", JSON.stringify(resultado));
              this.router.navigate(["/notificacao/visualizar"]);
            })
            .catch((erro) => this.errorHandler.handle(erro));
        }
      })
      .catch((erro) => this.errorHandler.handle(erro));
  }
}
